/**
 * Analytics Helper - Google Analytics 4 Events
 * Tracking de eventos SEO y conversión
 */

/* global gtag */

// Detectar y guardar fuente de tráfico
export const saveTrafficSource = () => {
  if (typeof window === 'undefined') return;
  
  // Solo guardar si es la primera vez en la sesión
  if (!sessionStorage.getItem('traffic_source')) {
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    
    let source = 'direct';
    
    // UTM parameters (highest priority)
    if (urlParams.get('utm_source')) {
      source = urlParams.get('utm_source');
    }
    // Google Search
    else if (referrer.includes('google.com') || referrer.includes('google.es')) {
      source = 'google_organic';
    }
    // Other search engines
    else if (referrer.includes('bing.com') || referrer.includes('yahoo.com')) {
      source = 'search_engine';
    }
    // Social media
    else if (referrer.includes('facebook.com') || referrer.includes('instagram.com') || 
             referrer.includes('twitter.com') || referrer.includes('linkedin.com')) {
      source = 'social';
    }
    // Other referrer
    else if (referrer && referrer !== '') {
      source = 'referral';
    }
    
    sessionStorage.setItem('traffic_source', source);
    sessionStorage.setItem('landing_page', window.location.pathname);
    sessionStorage.setItem('entry_time', Date.now().toString());
  }
};

// 1. Landing desde búsqueda orgánica
export const trackOrganicLanding = () => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source');
  
  if (source && source.includes('google') || source === 'search_engine') {
    gtag('event', 'organic_search_landing', {
      source: source,
      page_path: window.location.pathname,
      landing_page: sessionStorage.getItem('landing_page') || window.location.pathname,
      referrer: document.referrer || 'none'
    });
  }
};

// 2. Itinerario creado (CONVERSIÓN PRINCIPAL)
export const trackItineraryCreated = (destination, days, tipoViaje, ritmo) => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source') || 'direct';
  const fromBlog = sessionStorage.getItem('landing_page')?.includes('/blog/') || false;
  const timeToConversion = Date.now() - parseInt(sessionStorage.getItem('entry_time') || Date.now());
  
  gtag('event', 'itinerary_created', {
    source: source,
    destination: destination,
    days: days,
    tipoViaje: tipoViaje,
    ritmo: ritmo,
    from_blog: fromBlog,
    time_to_conversion_seconds: Math.floor(timeToConversion / 1000),
    value: 1 // Valor de conversión
  });
  
  // También trackear como conversión en GA4
  gtag('event', 'conversion', {
    send_to: 'AW-CONVERSION-ID', // Reemplazar con tu ID real
    value: 1.0,
    currency: 'EUR'
  });
};

// 3. Click en CTA desde blog
export const trackBlogCtaClick = (articleSlug, ctaText, destinationPage = '/planificador-ia/') => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source') || 'direct';
  
  gtag('event', 'blog_cta_click', {
    article_slug: articleSlug,
    cta_text: ctaText,
    destination_page: destinationPage,
    source: source,
    article_full_path: window.location.pathname
  });
};

// 4. Scroll depth (engagement)
export const trackScrollDepth = (percentage) => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source') || 'direct';
  const pagePath = window.location.pathname;
  
  // Solo trackear milestones importantes: 25%, 50%, 75%, 100%
  if ([25, 50, 75, 100].includes(percentage)) {
    gtag('event', 'scroll_depth', {
      page_path: pagePath,
      scroll_percentage: percentage,
      source: source,
      page_type: pagePath.includes('/blog/') ? 'blog' : 'app'
    });
  }
};

// 5. Descarga de PDF
export const trackPdfDownload = (destination, days) => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source') || 'direct';
  const timeToDownload = Date.now() - parseInt(sessionStorage.getItem('entry_time') || Date.now());
  
  gtag('event', 'pdf_download', {
    source: source,
    itinerary_destination: destination,
    itinerary_days: days,
    time_to_download_seconds: Math.floor(timeToDownload / 1000),
    value: 0.5 // Valor secundario de conversión
  });
};

// BONUS: Cambio de idioma
export const trackLanguageChange = (fromLang, toLang, pageType = 'unknown') => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  gtag('event', 'language_change', {
    from_lang: fromLang,
    to_lang: toLang,
    page_type: pageType,
    page_path: window.location.pathname
  });
};

// BONUS: Tiempo en página (calidad de visita)
export const trackTimeOnPage = (timeSeconds) => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source') || 'direct';
  const pagePath = window.location.pathname;
  
  // Solo trackear si pasa cierto umbral (ej: 30 segundos = visita de calidad)
  if (timeSeconds >= 30) {
    gtag('event', 'quality_visit', {
      page_path: pagePath,
      time_on_page_seconds: timeSeconds,
      source: source,
      page_type: pagePath.includes('/blog/') ? 'blog' : 'app'
    });
  }
};

// Hook personalizado para tracking de scroll
export const useScrollTracking = () => {
  if (typeof window === 'undefined') return;
  
  let scrollPercentages = new Set();
  
  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight - windowHeight;
    const scrolled = window.scrollY;
    const percentage = Math.round((scrolled / documentHeight) * 100);
    
    // Trackear en milestones
    if (percentage >= 25 && !scrollPercentages.has(25)) {
      scrollPercentages.add(25);
      trackScrollDepth(25);
    }
    if (percentage >= 50 && !scrollPercentages.has(50)) {
      scrollPercentages.add(50);
      trackScrollDepth(50);
    }
    if (percentage >= 75 && !scrollPercentages.has(75)) {
      scrollPercentages.add(75);
      trackScrollDepth(75);
    }
    if (percentage >= 99 && !scrollPercentages.has(100)) {
      scrollPercentages.add(100);
      trackScrollDepth(100);
    }
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => window.removeEventListener('scroll', handleScroll);
};

// Helper para tracking de tiempo en página
export const useTimeOnPage = () => {
  if (typeof window === 'undefined') return null;
  
  const entryTime = Date.now();
  
  return () => {
    const exitTime = Date.now();
    const timeSpent = Math.floor((exitTime - entryTime) / 1000);
    trackTimeOnPage(timeSpent);
  };
};

// 9. Click en enlace de afiliado (IATI Seguro, Trip.com, GetYourGuide)
export const trackAffiliateClick = (affiliate, position = 'unknown', additionalData = {}) => {
  if (typeof window === 'undefined' || typeof gtag === 'undefined') return;
  
  const source = sessionStorage.getItem('traffic_source') || 'direct';
  const fromBlog = sessionStorage.getItem('landing_page')?.includes('/blog/') || false;
  
  // Validar que el affiliate sea uno de los permitidos
  const allowedAffiliates = ['iati_seguro', 'getyourguide', 'tripcom', 'aviasales'];
  if (!allowedAffiliates.includes(affiliate)) {
    console.warn(`trackAffiliateClick: affiliate "${affiliate}" no es válido. Valores permitidos: ${allowedAffiliates.join(', ')}`);
    return;
  }
  
  gtag('event', 'affiliate_click', {
    affiliate: affiliate,                    // 'iati_seguro', 'getyourguide', 'tripcom'
    position: position,                      // 'banner_inline', 'widget_inline', 'button_inline', 'blog_section', etc.
    page_path: window.location.pathname,
    source: source,
    from_blog: fromBlog,
    page_type: window.location.pathname.includes('/blog/') ? 'blog' : 'app',
    ...additionalData                        // Permite pasar datos adicionales específicos
  });
};

export default {
  saveTrafficSource,
  trackOrganicLanding,
  trackItineraryCreated,
  trackBlogCtaClick,
  trackScrollDepth,
  trackPdfDownload,
  trackLanguageChange,
  trackTimeOnPage,
  trackAffiliateClick,
  useScrollTracking,
  useTimeOnPage
};
